import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usersAPI, challengesAPI, interviewsAPI } from '../../utils/api';
import AdminHeader from './AdminHeader';
import './SystemAnalytics.css';

const SystemAnalytics = ({ onNavigate, currentView }) => {
  const { isAdmin } = useAuth();
  const [analytics, setAnalytics] = useState({
    totalInterviews: 0,
    completionRate: 0,
    mostUsedChallenges: [],
    activeSessions: 0,
    challengesByDifficulty: {
      junior: 0,
      senior: 0,
      advanced: 0,
      redteam: 0,
    },
    userDistribution: {
      admin: 0,
      interviewer: 0,
      candidate: 0,
    },
    sessionActivity: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      return;
    }
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [usersRes, challengesRes, interviewsRes] = await Promise.all([
        usersAPI.getAll(),
        challengesAPI.getAll(),
        interviewsAPI.getAll(),
      ]);

      const users = usersRes.data.users || [];
      const challenges = challengesRes.data.challenges || [];
      const interviews = interviewsRes.data.interviews || [];

      // Calculate metrics
      const totalInterviews = interviews.length;
      const completedInterviews = interviews.filter(i => i.status === 'completed').length;
      const completionRate = totalInterviews > 0
        ? Math.round((completedInterviews / totalInterviews) * 100)
        : 0;

      // Active sessions
      const activeSessions = interviews.filter(i => i.status === 'in_progress').length;

      // Challenges by difficulty
      const challengesByDifficulty = {
        junior: challenges.filter(c => c.difficulty === 'junior').length,
        senior: challenges.filter(c => c.difficulty === 'senior').length,
        advanced: challenges.filter(c => c.difficulty === 'advanced').length,
        redteam: challenges.filter(c => c.difficulty === 'redteam').length,
      };

      // User distribution
      const userDistribution = {
        admin: users.filter(u => u.role === 'admin').length,
        interviewer: users.filter(u => u.role === 'interviewer').length,
        candidate: users.filter(u => u.role === 'candidate').length,
      };

      // Most used challenges (simulated - in real app would come from session data)
      const challengeUsageMap = {};
      interviews.forEach(interview => {
        if (interview.challenges && Array.isArray(interview.challenges)) {
          interview.challenges.forEach(challengeId => {
            challengeUsageMap[challengeId] = (challengeUsageMap[challengeId] || 0) + 1;
          });
        }
      });

      const mostUsedChallenges = Object.entries(challengeUsageMap)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([challengeId, count]) => {
          const challenge = challenges.find(c => c._id === challengeId);
          return {
            id: challengeId,
            title: challenge?.title || 'Unknown Challenge',
            count,
          };
        });

      // Session activity timeline (last 7 days)
      const sessionActivity = generateSessionActivity(interviews);

      setAnalytics({
        totalInterviews,
        completionRate,
        mostUsedChallenges,
        activeSessions,
        challengesByDifficulty,
        userDistribution,
        sessionActivity,
      });
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const generateSessionActivity = (interviews) => {
    const days = 7;
    const activity = [];
    const today = new Date();

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      date.setHours(0, 0, 0, 0);

      const dayStart = date.getTime();
      const dayEnd = dayStart + 86400000; // 24 hours in ms

      const count = interviews.filter(interview => {
        const createdAt = new Date(interview.createdAt).getTime();
        return createdAt >= dayStart && createdAt < dayEnd;
      }).length;

      activity.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        count,
      });
    }

    return activity;
  };

  const handleExportData = () => {
    // Create exportable data
    const exportData = {
      exportDate: new Date().toISOString(),
      analytics: analytics,
    };

    // Create blob and download
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sechire-analytics-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isAdmin()) {
    return (
      <div className="system-analytics">
        <div className="error-state">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="system-analytics">
        <div className="loading-state">
          <div className="cyber-spinner"></div>
          <p>Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="system-analytics">
      <AdminHeader currentView={currentView} onNavigate={onNavigate} />
      <div className="system-analytics-content">
        <div className="analytics-header">
          <h2>System Analytics</h2>
          <button className="cyber-btn primary" onClick={handleExportData}>
            Export Data
          </button>
        </div>

        {error && (
          <div className="error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">total.interviews</span>
          </div>
          <div className="metric-content">
            <div className="metric-value glow-cyan">{analytics.totalInterviews}</div>
            <div className="metric-label">Total Interviews Conducted</div>
          </div>
        </div>

        <div className="metric-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">completion.rate</span>
          </div>
          <div className="metric-content">
            <div className="metric-value glow-green">{analytics.completionRate}%</div>
            <div className="metric-label">Average Completion Rate</div>
          </div>
        </div>

        <div className="metric-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">active.sessions</span>
          </div>
          <div className="metric-content">
            <div className="metric-value text-cyber">{analytics.activeSessions}</div>
            <div className="metric-label">Active Sessions Count</div>
          </div>
        </div>

        <div className="metric-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">most.used</span>
          </div>
          <div className="metric-content">
            <div className="metric-value text-neon">
              {analytics.mostUsedChallenges.length > 0 ? analytics.mostUsedChallenges[0].count : 0}
            </div>
            <div className="metric-label">Top Challenge Usage</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        {/* Challenges by Difficulty */}
        <div className="chart-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">challenges.by.difficulty</span>
          </div>
          <div className="chart-content">
            <h4>Challenges by Difficulty</h4>
            <div className="bar-chart">
              {Object.entries(analytics.challengesByDifficulty).map(([difficulty, count]) => {
                const maxCount = Math.max(...Object.values(analytics.challengesByDifficulty), 1);
                const percentage = (count / maxCount) * 100;
                return (
                  <div key={difficulty} className="bar-row">
                    <div className="bar-label">{difficulty}</div>
                    <div className="bar-container">
                      <div
                        className={`bar ${difficulty}`}
                        style={{ width: `${percentage}%` }}
                      >
                        <span className="bar-value">{count}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* User Distribution */}
        <div className="chart-card terminal-card">
          <div className="terminal-header">
            <div className="terminal-dots">
              <span className="terminal-dot red"></span>
              <span className="terminal-dot yellow"></span>
              <span className="terminal-dot green"></span>
            </div>
            <span className="terminal-title">user.distribution</span>
          </div>
          <div className="chart-content">
            <h4>User Distribution</h4>
            <div className="pie-chart">
              {Object.entries(analytics.userDistribution).map(([role, count]) => {
                const total = Object.values(analytics.userDistribution).reduce((a, b) => a + b, 0);
                const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                  <div key={role} className="pie-row">
                    <div className="pie-indicator">
                      <div className={`pie-dot ${role}`}></div>
                      <span className="pie-label">{role}</span>
                    </div>
                    <div className="pie-stats">
                      <span className="pie-count">{count}</span>
                      <span className="pie-percentage">({percentage}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Session Activity Timeline */}
      <div className="timeline-card terminal-card">
        <div className="terminal-header">
          <div className="terminal-dots">
            <span className="terminal-dot red"></span>
            <span className="terminal-dot yellow"></span>
            <span className="terminal-dot green"></span>
          </div>
          <span className="terminal-title">session.activity</span>
        </div>
        <div className="chart-content">
          <h4>Session Activity (Last 7 Days)</h4>
          <div className="timeline-chart">
            {analytics.sessionActivity.map((day, index) => {
              const maxCount = Math.max(...analytics.sessionActivity.map(d => d.count), 1);
              const height = (day.count / maxCount) * 100;
              return (
                <div key={index} className="timeline-bar">
                  <div className="timeline-bar-container">
                    <div
                      className="timeline-bar-fill"
                      style={{ height: `${height}%` }}
                      title={`${day.count} sessions`}
                    >
                      <span className="timeline-value">{day.count}</span>
                    </div>
                  </div>
                  <div className="timeline-label">{day.date}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Most Used Challenges */}
      <div className="top-challenges-card terminal-card">
        <div className="terminal-header">
          <div className="terminal-dots">
            <span className="terminal-dot red"></span>
            <span className="terminal-dot yellow"></span>
            <span className="terminal-dot green"></span>
          </div>
          <span className="terminal-title">top.challenges</span>
        </div>
        <div className="chart-content">
          <h4>Most Used Challenges</h4>
          {analytics.mostUsedChallenges.length === 0 ? (
            <div className="empty-state">
              <p>No challenge usage data available</p>
            </div>
          ) : (
            <div className="challenges-list">
              {analytics.mostUsedChallenges.map((challenge, index) => (
                <div key={challenge.id} className="challenge-row">
                  <div className="challenge-rank">#{index + 1}</div>
                  <div className="challenge-info">
                    <div className="challenge-name">{challenge.title}</div>
                    <div className="challenge-usage">Used {challenge.count} times</div>
                  </div>
                  <div className="challenge-count">
                    <span className="cyber-badge success">{challenge.count}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default SystemAnalytics;
